import { useQuery } from "@tanstack/react-query";
import { IN_STOCK_STATUSES } from "@/lib/partStatus";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentCompany } from "@/hooks/useCurrentCompany";

export interface SparePartRow {
  id: string;
  company_id: string;
  user_id: string;
  appliance_id: string | null;
  component_id: string | null;
  name: string;
  brand: string | null;
  manufacturer: string | null;
  model_number: string | null;
  serial_no: string | null;
  qr_token: string;
  bin_id: string | null;
  part_number?: string | null;
  status: string;
  weight_kg: number | null;
  estimated_value: number | null;
  notes: string | null;
  harvested_at: string;
  created_at: string;
  extraction_status?: string | null;
  extraction_photo_url?: string | null;
  appliance_category?: string | null;
  appliance_serial_number?: string | null;
  part_type_id?: string | null;
  parts_category_name?: string | null;
}

export function useSpareParts() {
  const { id: companyId } = useCurrentCompany();
  return useQuery({
    queryKey: ["spare_parts", companyId],
    enabled: !!companyId,
    queryFn: async (): Promise<SparePartRow[]> => {
      const { data, error } = await supabase
        .from("spare_parts")
        .select(
          "*, appliances:appliance_id(category, serial_number), spare_part_types:part_type_id(name, harvest_components:parts_category_id(name))",
        )
        .eq("company_id", companyId!)
        .eq("extraction_status", "extracted")
        .order("harvested_at", { ascending: false });
      if (error) throw error;
      return ((data || []) as any[]).map((r) => ({
        ...r,
        appliance_category: r.appliances?.category ?? null,
        appliance_serial_number: r.appliances?.serial_number ?? null,
        parts_category_name: r.spare_part_types?.harvest_components?.name ?? null,
      })) as SparePartRow[];
    },
  });
}


export function useSparePartsByIds(ids: string[]) {
  return useQuery({
    queryKey: ["spare_parts_by_ids", ids.join(",")],
    enabled: ids.length > 0,
    queryFn: async (): Promise<SparePartRow[]> => {
      const { data, error } = await supabase
        .from("spare_parts")
        .select("*, spare_part_types:part_type_id(parts_category_id, harvest_components:parts_category_id(name))")
        .in("id", ids)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return ((data || []) as any[]).map((r) => ({
        ...r,
        parts_category_name: r.spare_part_types?.harvest_components?.name ?? null,
      })) as SparePartRow[];
    },
  });
}

export function useSparePartsByBin(binId: string | undefined) {
  const { id: companyId } = useCurrentCompany();
  return useQuery({
    queryKey: ["spare_parts_by_bin", binId, companyId],
    enabled: !!binId && !!companyId,
    queryFn: async (): Promise<SparePartRow[]> => {
      const { data, error } = await supabase
        .from("spare_parts")
        .select("*, appliances:appliance_id(brand, model_number, category), spare_part_types:part_type_id(name, harvest_components:parts_category_id(name))")
        .eq("company_id", companyId!)
        .eq("bin_id", binId!)
        .eq("extraction_status", "extracted")
        .in("status", [...IN_STOCK_STATUSES])
        .order("harvested_at", { ascending: false });
      if (error) throw error;
      return ((data || []) as any[]).map((r) => ({
        ...r,
        appliance_category: r.appliances?.category ?? null,
        parts_category_name: r.spare_part_types?.harvest_components?.name ?? null,
      })) as SparePartRow[];
    },
  });
}
